import intersection from 'lodash/intersection';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { createResourceLocatorString, matchPathname } from '../../util/routes';
import {
  isAnyFilterActive,
  parseSelectFilterOptions,
  constructQueryParamName,
} from '../../util/search';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { normalizeIncomingSearchParams } from '../../util/searchParamMappers';
import { getSeoByCategoryIds, getSlugSearchParams } from '../../util/categorySlugMap';
import {
  getStartOf,
  parseDateFromISO8601,
  subtractTime,
  addTime,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isFieldForCategory, isFieldForListingType } from '../../util/fieldHelpers';

export const isPromotionsPath = pathname => (pathname || '').startsWith('/promotions');

const validURLParamForCategoryData = (prefix, categories, level, params) => {
  const levelKey = constructQueryParamName(`${prefix}${level}`, 'public');
  const levelValue = params?.[levelKey];
  const foundCategory = categories.find(cat => cat.id === params?.[levelKey]);
  const subcategories = foundCategory?.subcategories || [];
  return foundCategory && subcategories.length > 0
    ? {
        [levelKey]: levelValue,
        ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
      }
    : foundCategory
    ? { [levelKey]: levelValue }
    : {};
};

const validURLParamForListingTypeData = (listingTypes, param) => {
  const listingTypeValue = param?.pub_listingType;
  const foundListingType = listingTypes.find(lt => lt === listingTypeValue);
  return foundListingType && listingTypeValue ? { pub_listingType: listingTypeValue } : {};
};

/**
 * Omit those listing field parameters, that are not allowed with current category selection
 *
 * @param {Object} searchParams current search params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @returns search parameters without currently restricted listing fields
 */
export const omitLimitedListingFieldParams = (searchParams, filterConfigs) => {
  const {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams = {},
  } = filterConfigs;

  const { listingType: listingTypePathParam } = currentPathParams;
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const listingTypeSearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'listingType');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, searchParams)
    : {};

  const validListingTypeParamNames =
    activeListingTypes && listingTypeSearchConfig
      ? validURLParamForListingTypeData(activeListingTypes, searchParams)
      : {};

  return Object.entries(searchParams).reduce((picked, searchParam) => {
    const [searchParamKey, searchParamValue] = searchParam;
    const foundConfig = listingFieldsConfig.find(
      f => constructQueryParamName(f.key, f.scope) === searchParamKey
    );
    const currentCategories = Object.values(validNestedCategoryParamNames);
    const isForCategory = isFieldForCategory(currentCategories, foundConfig);
    const currentListingType = listingTypePathParam
      ? [listingTypePathParam]
      : Object.values(validListingTypeParamNames);
    const isForListingType = isFieldForListingType(currentListingType, foundConfig);
    const searchParamMaybe =
      !foundConfig || (foundConfig && isForCategory && isForListingType)
        ? { [searchParamKey]: searchParamValue }
        : {};
    return { ...picked, ...searchParamMaybe };
  }, {});
};

/**
 * Validates a filter search param against the default and extended data configuration of listings.
 *
 * All invalid param names and values are dropped
 *
 * @param {String} queryParamName Search parameter name
 * @param {Object} paramValue Search parameter value
 * @param {Object} listingFieldFilters extended data configuration with indexForSearch === true
 * @param {Object} defaultFilters configuration for default built-in filters.
 */
export const validURLParamForExtendedData = (
  queryParamName,
  paramValueRaw,
  listingFieldFilters,
  defaultFilters
) => {
  const paramValue = paramValueRaw.toString();

  // Price is built-in filter for listing entities
  if (queryParamName === 'price') {
    // Restrict price range to correct min & max
    const { min, max } = defaultFilters.find(conf => conf.schemaType === 'price') || {};
    const valueArray = paramValue ? paramValue.split(',') : [];
    const validValues = valueArray.map(v => {
      return v < min ? min : v > max ? max : v;
    });
    return validValues.length === 2 ? { [queryParamName]: validValues.join(',') } : {};
  } else if (queryParamName === 'keywords') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'dates') {
    const searchTZ = 'Etc/UTC';
    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const dates = paramValue ? paramValue.split(',') : [];
    const hasValues = dates.length > 0;
    const startDate = hasValues ? parseDateFromISO8601(dates[0], searchTZ) : null;
    const endDate = hasValues ? parseDateFromISO8601(dates[1], searchTZ) : null;
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    return hasValidDates ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'seats') {
    return paramValue ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'pub_listingType') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  }

  // Resolve configurations for extended data filters
  const listingFieldFilterConfig = listingFieldFilters.find(
    f => queryParamName === constructQueryParamName(f.key, f.scope)
  );

  if (listingFieldFilterConfig) {
    const { schemaType, enumOptions = [], filterConfig } = listingFieldFilterConfig;
    if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType)) {
      const isSchemaTypeMultiEnum = schemaType === SCHEMA_TYPE_MULTI_ENUM;
      const searchMode = filterConfig?.searchMode || 'has_any';

      // Pick valid select options only
      const valueArray = parseSelectFilterOptions(paramValue);
      const allowedValues = enumOptions.map(o => `${o.option}`);
      const validValues = intersection(valueArray, allowedValues).join(',');

      return validValues.length > 0
        ? {
            [queryParamName]: isSchemaTypeMultiEnum ? `${searchMode}:${validValues}` : validValues,
          }
        : {};
    } else {
      // Generic filter - remove empty params
      return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
    }
  }
  return {};
};

/**
 * Checks filter param value validity.
 *
 * The URL params that are not part of listing.query filters are dropped by default.
 *
 * @param {Object} params Search query params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @param {boolean} dropNonFilterParams if false, extra params are passed through.
 */
export const validFilterParams = (params, filterConfigs, dropNonFilterParams = true) => {
  const { listingFieldsConfig, defaultFiltersConfig, listingCategories } = filterConfigs;

  const listingFieldFiltersConfig = listingFieldsConfig.filter(
    config => config.filterConfig?.indexForSearch
  );
  const listingFieldParamNames = listingFieldFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  // Note: builtInFilterParamNames might include categoryLevel,
  //       even though it isn't a paramname that's used with nested category tree.
  //       (pub_categoryLevel1, pub_categoryLevel2, and pub_categoryLevel3 are used instead.)
  const builtInFilterParamNames = defaultFiltersConfig.map(f => {
    return ['category', 'listingType'].includes(f.schemaType) ? `pub_${f.key}` : f.key;
  });
  const filterParamNames = [...listingFieldParamNames, ...builtInFilterParamNames];

  // Note: currently, we only support nested enums with a single default filter
  //       that has schema type: "category"
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, params)
    : {};
  const isParamNameNestedEnumRelated = (paramName, key, isNestedEnum) => {
    return isNestedEnum && key ? paramName.indexOf(key) > -1 : false;
  };

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(params, filterConfigs);
  const paramEntries = Object.entries(unlimitedSearchParams);

  const listingFieldsAndBuiltInFilterParamNames = paramEntries.reduce((validParams, entry) => {
    const [paramName, paramValue] = entry;
    const isIndependentParam = filterParamNames.includes(paramName);
    const isNestedEnum = isIndependentParam
      ? false
      : isParamNameNestedEnumRelated(
          paramName,
          categorySearchConfig?.key,
          categorySearchConfig?.isNestedEnum
        );
    return isIndependentParam
      ? {
          ...validParams,
          ...validURLParamForExtendedData(
            paramName,
            paramValue,
            listingFieldFiltersConfig,
            defaultFiltersConfig
          ),
        }
      : dropNonFilterParams || isNestedEnum
      ? { ...validParams }
      : { ...validParams, [paramName]: paramValue };
  }, {});

  // TODO: Currently this only supports categoryLevel with nested param names.
  //       This needs more work to make other enum fields to understand nested keys.
  return { ...listingFieldsAndBuiltInFilterParamNames, ...validNestedCategoryParamNames };
};

/**
 * Helper to pick only valid values of search params from URL (location)
 * Note: location.search might look like: '?pub_category=men&pub_amenities=towels,bathroom'
 *
 * @param {Object} props object containing: location and (app) config
 * @returns picked search params against extended data config and default filter config
 */
export const validUrlQueryParamsFromProps = props => {
  const { location, config, params: currentPathParams = {} } = props;
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const listingCategories = config.categoryConfiguration.categories;
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams,
  };

  // eslint-disable-next-line no-unused-vars
  const { mapSearch, page, deals, ...rawSearchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const slugSearchParams = getSlugSearchParams(currentPathParams, config);
  const mergedSearchParams = { ...rawSearchInURL, ...slugSearchParams };
  const searchInURLRaw = normalizeIncomingSearchParams(mergedSearchParams, filterConfigs);
  // Allow brand/category/deviceType public params even if not listed as configured filters yet,
  // but avoid keeping duplicate public params when their internal counterparts exist.
  const passthroughAllowed = ['brand', 'category', 'subcategory', 'subsubcategory', 'deviceType'];
  const searchInURL = Object.entries(mergedSearchParams).reduce((acc, [k, v]) => {
    if (!passthroughAllowed.includes(k)) return acc;
    // Map public param to its internal counterpart to check duplicates
    const internalKey =
      k === 'brand'
        ? 'pub_brand'
        : k === 'category'
        ? 'pub_categoryLevel1'
        : k === 'subcategory'
        ? 'pub_categoryLevel2'
        : k === 'subsubcategory'
        ? 'pub_categoryLevel3'
        : k === 'deviceType'
        ? 'pub_deviceType'
        : null;
    // Only add passthrough param if internal equivalent is NOT present
    return internalKey && acc.hasOwnProperty(internalKey) ? acc : { ...acc, [k]: v };
  }, searchInURLRaw);
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  return validFilterParams(searchInURL, filterConfigs, false);
};

/**
 * Helper to figure out initialValues for Final Form that handles search filters
 *
 * @param {Object} props object containing: location, listingFieldsConfig, defaultFiltersConfig
 * @param {Object} currentQueryParams object containing current state of queryParams (used only when isLiveEdit is false)
 * @returns a function with params queryParamNames, and isLiveEdit.
 *          It's called from FilterComponent and it returns initial values for the filter.
 */
export const initialValues = (props, currentQueryParams) => (queryParamNames, isLiveEdit) => {
  const urlQueryParams = validUrlQueryParamsFromProps(props);

  // Get initial value for a given parameter from state if its there.
  const getInitialValue = paramName => {
    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParam = currentQueryParams[paramName];
    const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
    return hasQueryParamInState && !isLiveEdit ? currentQueryParam : urlQueryParams[paramName];
  };

  // Return all the initial values related to given queryParamNames
  // InitialValues for "amenities" filter could be
  // { amenities: "has_any:towel,jacuzzi" }
  const isArray = Array.isArray(queryParamNames);
  return isArray
    ? queryParamNames.reduce((acc, paramName) => {
        const initValue = getInitialValue(paramName);
        const addInitialValueMaybe = initValue ? { [paramName]: initValue } : {};
        return { ...acc, ...addInitialValueMaybe };
      }, {})
    : {};
};

/**
 * Some parameters could conflict with sort. If sortConfig defines conflictingFilters,
 * This function checks if they are active and returns "sort" param as null
 *
 * @param {*} searchParams
 * @param {*} filterConfigs contains config like listingFieldsConfig and defaultFiltersConfig
 * @param {*} sortConfig
 * @returns sort parameter as null if sortConfig defines conflictingFilters
 */
export const cleanSearchFromConflictingParams = (searchParams, filterConfigs, sortConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfigs
  );

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(searchParams, filterConfigs);

  return sortingFiltersActive
    ? { ...unlimitedSearchParams, [sortConfig.queryParamName]: null }
    : unlimitedSearchParams;
};

/**
 * Extract search parameters, including a custom URL params,
 * which are validated by mapping the values to marketplace custom config.
 *
 * @param {Object} params Search query params
 * @param {Object} listingFieldsConfig extended data configuration with indexForSearch === true
 * @param {Object} defaultFiltersConfig configuration for default built-in filters.
 * @param {Object} sortConfig config for sort search results feature
 * @param {boolean} isOriginInUse if origin is in use, return it too.
 */
export const pickSearchParamsOnly = (params, filterConfigs, sortConfig, isOriginInUse) => {
  const { address, origin, bounds, ...rest } = params || {};
  const boundsMaybe = bounds ? { bounds } : {};
  const originMaybe = isOriginInUse && origin ? { origin } : {};
  const filterParams = validFilterParams(rest, filterConfigs);
  const sort = rest[sortConfig.queryParamName];
  const sortMaybe = sort ? { sort } : {};

  return {
    ...boundsMaybe,
    ...originMaybe,
    ...filterParams,
    ...sortMaybe,
  };
};

/**
 * This helper has 2 functions:
 * - It picks search params from Location instance (location.search)
 * - It verifies that those search params are the same as search params in state.
 *   In some cases, search params are referencing previous params
 *   and listings should not be considered loaded.
 *
 * @param {Object} searchFromLocation searchParams from URL (location.search)
 * @param {Object} searchParamsInProps searchParams from store
 * @param {Object} listingFieldsConfig config for listing's extended data
 * @param {Object} defaultFiltersConfig config for default filters
 * @param {Object} sortConfig config for SortBy feature
 * @returns object containing
 *   1. searchParamsInURL (omit pagination 'page' or 'mapSearch'),
 *   2. urlQueryParams (picked valid search params for listing query), and
 *   3. searchParamsAreInSync is true if searchFromLocation and searchParamsInProps match.
 */
export const searchParamsPicker = (
  searchFromLocation,
  searchParamsInProps,
  filterConfigs,
  sortConfig,
  isOriginInUse,
  currentPathParams = {},
  config = {}
) => {
  const { mapSearch, page, ...rawSearchParamsInURL } = parse(searchFromLocation, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const slugSearchParams = getSlugSearchParams(currentPathParams, config);
  const mergedSearchParams = { ...rawSearchParamsInURL, ...slugSearchParams };
  const normalizedFromURL = normalizeIncomingSearchParams(mergedSearchParams, filterConfigs);
  // Allow custom passthrough params for meta enrichment
  const passthroughAllowed = ['brand', 'category', 'subcategory', 'subsubcategory', 'deviceType'];
  const passthrough = Object.entries(mergedSearchParams).reduce((acc, [k, v]) => {
    return passthroughAllowed.includes(k) ? { ...acc, [k]: v } : acc;
  }, {});
  const searchParamsInURL = { ...normalizedFromURL, ...passthrough };

  // Pick only search params that are part of current search configuration
  const queryParamsFromSearchParams = pickSearchParamsOnly(
    searchParamsInProps,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );
  // Pick only search params that are part of current search configuration
  const queryParamsFromURL = pickSearchParamsOnly(
    searchParamsInURL,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );

  // Page transition might initially use values from previous search
  const searchParamsAreInSync =
    stringify(queryParamsFromURL) === stringify(queryParamsFromSearchParams);

  return {
    urlQueryParams: queryParamsFromURL,
    searchParamsInURL,
    searchParamsAreInSync,
  };
};

export const pickListingFieldFilters = params => {
  const {
    listingFields,
    locationSearch,
    categoryConfiguration,
    activeListingTypes,
    currentPathParams = {},
  } = params;
  const searchParams = parse(locationSearch);
  const categories = categoryConfiguration.categories;
  const validNestedCategoryParamNames = categories
    ? validURLParamForCategoryData(categoryConfiguration.key, categories, 1, searchParams)
    : {};

  const listingTypeFromSlug =
    currentPathParams?.slug && activeListingTypes
      ? activeListingTypes.find(lt => lt === currentPathParams.slug)
      : null;
  const listingTypeParamMaybe = listingTypeFromSlug ? { pub_listingType: listingTypeFromSlug } : {};
  const validListingTypeParamNames = activeListingTypes
    ? validURLParamForListingTypeData(activeListingTypes, {
        ...searchParams,
        ...listingTypeParamMaybe,
      })
    : {};

  const currentCategories = Object.values(validNestedCategoryParamNames);
  const currentListingType = Object.values(validListingTypeParamNames);
  const pickedFields = listingFields.reduce((picked, fieldConfig) => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    const isTargetListingField = isFieldForListingType(currentListingType, fieldConfig);
    return isTargetCategory && isTargetListingField ? [...picked, fieldConfig] : picked;
  }, []);
  return pickedFields;
};
/**
 * Returns listing fields (extended data configs) grouped into arrays. [primaryConfigArray, secondaryConfigArray]
 * @param {Object} configs listing extended data config
 * @param {Array<String>} activeListingTypes select configs that are marked only for these active listing types
 * @returns Array of grouped arrays. First subarray contains primary configs and the second contains secondary configs.
 */
export const groupListingFieldConfigs = (configs, activeListingTypes) =>
  configs.reduce(
    (grouped, config) => {
      const [primary, secondary] = grouped;
      const { listingTypeConfig = {}, filterConfig } = config;
      const isIndexed = filterConfig?.indexForSearch === true;
      const isActiveListingTypes =
        !listingTypeConfig.limitToListingTypeIds ||
        listingTypeConfig.listingTypeIds.some(lt => activeListingTypes.includes(lt));
      const isPrimary = filterConfig?.group === 'primary';
      return isActiveListingTypes && isIndexed && isPrimary
        ? [[...primary, config], secondary]
        : isActiveListingTypes && isIndexed
        ? [primary, [...secondary, config]]
        : grouped;
    },
    [[], []]
  );

export const createSearchResultSchema = (
  listings,
  mainSearchData,
  intl,
  routeConfiguration,
  config
) => {
  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const marketplaceName = config.marketplaceName;

  // Helpers for brand/category enrichment in meta
  const toTitleCase = s =>
    (s || '')
      .toString()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\w\S*/g, t => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());

  const findCategoryLabelById = (categories, id) => {
    if (!id || !categories) return null;
    for (const c of categories) {
      if (c.id === id) return c.label || c.id;
      const sub = findCategoryLabelById(c.subcategories || [], id);
      if (sub) return sub;
    }
    return null;
  };

  // Dynamic title based on category/brand/subcategory filters
  const categoryParam = mainSearchData?.pub_categoryLevel1 || mainSearchData?.category;
  const subcategoryParam = mainSearchData?.pub_categoryLevel2 || mainSearchData?.subcategory;
  let brandParam = mainSearchData?.pub_brand || mainSearchData?.brand;
  const isDealsActive =
    mainSearchData?.deals === 'true' ||
    mainSearchData?.deals === true ||
    mainSearchData?.deals === '1' ||
    mainSearchData?.deals === 1;

  // Clean brand parameter: remove search mode prefix (e.g., "has_any:" or "has_all:")
  if (brandParam && brandParam.includes(':')) {
    brandParam = brandParam.split(':')[1];
  }

  // If multiple brands (comma-separated), don't use brand in title
  if (brandParam && brandParam.includes(',')) {
    brandParam = null;
  }

  const categorySEOData = getSeoByCategoryIds(categoryParam, subcategoryParam);

  // Generate dynamic title and description based on filters
  let schemaTitle = isDealsActive
    ? 'Nos bons plans & Promotions | Neokasa'
    : 'Tous les produits domotique et maison connectée | Neokasa';
  let schemaDescription = isDealsActive
    ? 'Retrouvez tous nos bons plans et promotions sur les produits domotiques et maison connectee.'
    : 'Découvrez notre catalogue complet de produits domotiques et maison connectée. Comparez les fonctionnalités, la compatibilité et les prix pour construire votre maison intelligente.';
  let searchTitle = isDealsActive
    ? 'Nos bons plans & Promotions'
    : 'Tous les produits domotique et maison connectée';
  const baseCategoryTitle = categorySEOData?.title || categorySEOData?.h1;

  // Priority 1: Subcategory + Brand
  if (subcategoryParam && brandParam && categorySEOData) {
    const brandName = toTitleCase(brandParam);
    schemaTitle = `${categorySEOData.h1} ${brandName} | Neokasa`;
    schemaDescription = `${categorySEOData.h1} de la marque ${brandName}. ${categorySEOData.metaDescription}`;
    searchTitle = `${categorySEOData.h1} ${brandName}`;
  }
  // Priority 2: Subcategory only
  else if (subcategoryParam && categorySEOData && !brandParam) {
    schemaTitle = baseCategoryTitle || categorySEOData.h1;
    schemaDescription = categorySEOData.metaDescription;
    searchTitle = categorySEOData.h1;
  }
  // Priority 3: Category + Brand
  else if (categoryParam && brandParam && categorySEOData && !subcategoryParam) {
    const brandName = toTitleCase(brandParam);
    schemaTitle = `${categorySEOData.h1} ${brandName} | Neokasa`;
    schemaDescription = `${categorySEOData.h1} de la marque ${brandName}. ${categorySEOData.metaDescription}`;
    searchTitle = `${categorySEOData.h1} ${brandName}`;
  }
  // Priority 4: Category only
  else if (categoryParam && categorySEOData && !subcategoryParam && !brandParam) {
    schemaTitle = baseCategoryTitle || categorySEOData.h1;
    schemaDescription = categorySEOData.metaDescription;
    searchTitle = categorySEOData.h1;
  }
  // Priority 5: Brand only
  else if (brandParam && !categoryParam && !subcategoryParam) {
    const brandName = toTitleCase(brandParam);
    schemaTitle = `Produits ${brandName} pour Maison Connectée | Neokasa`;
    schemaDescription = `Découvrez tous les produits ${brandName} pour votre maison connectée. Large sélection d'appareils intelligents ${brandName}. Livraison gratuite en France.`;
    searchTitle = `Produits ${brandName}`;
  }

  const schemaListings = listings.map((l, i) => {
    const title = l.attributes.title;
    const pathToItem = createResourceLocatorString('ListingPage', routeConfiguration, {
      id: l.id.uuid,
      slug: createSlug(title),
    });
    return {
      '@type': 'ListItem',
      position: i,
      url: `${config.marketplaceRootURL}${pathToItem}`,
      name: title,
    };
  });

  const schemaMainEntity = JSON.stringify({
    '@type': 'ItemList',
    name: searchTitle,
    itemListOrder: 'http://schema.org/ItemListOrderAscending',
    itemListElement: schemaListings,
  });
  return {
    title: schemaTitle,
    description: schemaDescription,
    schema: {
      '@context': 'http://schema.org',
      '@type': 'SearchResultsPage',
      description: schemaDescription,
      name: schemaTitle,
      mainEntity: [schemaMainEntity],
    },
  };
};

export const getDatesAndSeatsMaybe = (currentParams, newParams) => {
  const { seats, dates: newDates } = newParams;
  const { dates: currentDates } = currentParams;

  // Determine which dates and seats to use:
  // - if newDates has a value, it was just selected
  // - if newDates is null, it was just cleared
  // - if newDates is undefined, it was not modified, and we use currentDates
  const dates = !!newDates || newDates === null ? newDates : currentDates;

  const today = stringifyDateToISO8601(new Date());
  const aWeekFromNow = stringifyDateToISO8601(addTime(today, 7, 'day'));
  // Get parameters for dates and seats:
  // - If both dates and seats are included, pass both
  // - Dates can be queried without seats
  // - Seats cannot be queried without dates – pass a default date range
  //   of one week with the provided seats value
  // - If neither dates nor seats exist, set them to null to clear them from search
  const datesAndSeatsMaybe =
    dates && seats
      ? { dates, seats }
      : dates
      ? { dates }
      : seats
      ? { seats, dates: `${today},${aWeekFromNow}` }
      : { seats: null, dates: null };
  return datesAndSeatsMaybe;
};

/**
 * Returns params for createResourceLocatorString function based on the current
 * location and route configuration
 * @param {*} routes current route configuration
 * @param {*} location current ReactRouter location
 * @returns an object with the attributes routeName and pathParams, which can then be passed
 * as the corresponding parameters to createResourceLocatorString
 */
export const getSearchPageResourceLocatorStringParams = (routes, location) => {
  const matchedRoutes = matchPathname(location.pathname, routes);
  const searchPageRoute = 'SearchPage';
  const searchPageSlugRoute = 'SearchPageWithSlug';
  const promotionsPageRoute = 'PromotionsPage';

  if (matchedRoutes.length > 0) {
    const matched = matchedRoutes[0];
    const { params: pathParams, route } = matched;
    const routeName =
      route.name === promotionsPageRoute
        ? promotionsPageRoute
        : route.name === searchPageSlugRoute
        ? searchPageSlugRoute
        : searchPageRoute;

    return {
      routeName,
      pathParams,
    };
  } else {
    return {
      routeName: isPromotionsPath(location.pathname) ? promotionsPageRoute : searchPageRoute,
      pathParams: {},
    };
  }
};

export const getActiveListingTypes = (config, listingTypePathParam) => {
  const availableListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const activeListingTypes = listingTypePathParam
    ? availableListingTypes.filter(lt => lt === listingTypePathParam)
    : availableListingTypes;
  return { activeListingTypes };
};

/**
 * Get the display name for the currently active category filter
 *
 * @param {Object} urlQueryParams - Current URL query parameters
 * @param {Array} listingCategories - Array of category configurations
 * @returns {string|null} - The category name or null if no category filter is active
 */
export const getActiveCategoryName = (urlQueryParams, listingCategories) => {
  if (!urlQueryParams || !listingCategories || listingCategories.length === 0) {
    return null;
  }

  // Helper function to recursively search for category by ID
  const findCategoryById = (categories, categoryId) => {
    for (const category of categories) {
      if (category.id === categoryId) {
        return category;
      }
      if (category.subcategories && category.subcategories.length > 0) {
        const found = findCategoryById(category.subcategories, categoryId);
        if (found) return found;
      }
    }
    return null;
  };

  // Check for category filters in order of preference (deepest level first)
  // pub_categoryLevel3 > pub_categoryLevel2 > pub_categoryLevel1
  const categoryLevel3 = urlQueryParams.pub_categoryLevel3;
  const categoryLevel2 = urlQueryParams.pub_categoryLevel2;
  const categoryLevel1 = urlQueryParams.pub_categoryLevel1;

  if (categoryLevel3) {
    const category = findCategoryById(listingCategories, categoryLevel3);
    if (category) return category.name;
  }

  if (categoryLevel2) {
    const category = findCategoryById(listingCategories, categoryLevel2);
    if (category) return category.name;
  }

  if (categoryLevel1) {
    const category = findCategoryById(listingCategories, categoryLevel1);
    if (category) return category.name;
  }

  return null;
};

/**
 * Get the display name for the currently active brand filter
 *
 * @param {Object} urlQueryParams - Current URL query parameters
 * @param {Array} listingFieldsConfig - Listing fields configuration with enumOptions
 * @returns {string|null} - The brand name or null if no brand filter is active or multiple brands selected
 */
export const getActiveBrandName = (urlQueryParams, listingFieldsConfig) => {
  if (!urlQueryParams) {
    return null;
  }

  // Check for brand filter (pub_brand or brand passthrough param)
  let brand = urlQueryParams.pub_brand || urlQueryParams.brand;

  if (brand) {
    // Remove search mode prefix (e.g., "has_any:" or "has_all:")
    if (brand.includes(':')) {
      brand = brand.split(':')[1];
    }

    // If multiple brands (comma-separated), return null to use default title
    if (brand.includes(',')) {
      return null;
    }

    // Find the brand field config and get the label
    if (listingFieldsConfig) {
      const brandField = listingFieldsConfig.find(f => f.key === 'brand');
      if (brandField && brandField.enumOptions) {
        const option = brandField.enumOptions.find(opt => opt.option === brand);
        if (option && option.label) {
          return option.label;
        }
      }
    }

    // Fallback: Capitalize first letter of brand name and replace dashes with spaces
    return brand
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return null;
};

/**
 * Get the display name for the currently active connectivity filter
 *
 * @param {Object} urlQueryParams - Current URL query parameters
 * @param {Array} listingFieldsConfig - Listing fields configuration with enumOptions
 * @returns {string|null} - The connectivity name or null if no connectivity filter is active or multiple selected
 */
export const getActiveConnectivityName = (urlQueryParams, listingFieldsConfig) => {
  if (!urlQueryParams || !listingFieldsConfig) {
    return null;
  }

  // Check for connectivity filter
  let connectivity = urlQueryParams.pub_connectivity || urlQueryParams.connectivity;

  if (connectivity) {
    // Remove search mode prefix (e.g., "has_any:" or "has_all:")
    if (connectivity.includes(':')) {
      connectivity = connectivity.split(':')[1];
    }

    // If multiple connectivities (comma-separated), return null to use default title
    if (connectivity.includes(',')) {
      return null;
    }

    // Find the connectivity field config and get the label
    const connectivityField = listingFieldsConfig.find(f => f.key === 'connectivity');
    if (connectivityField && connectivityField.enumOptions) {
      const option = connectivityField.enumOptions.find(opt => opt.option === connectivity);
      if (option && option.label) {
        return option.label;
      }
    }

    // Fallback: Capitalize first letter and replace dashes with spaces
    return connectivity
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return null;
};

/**
 * Get the display name for the currently active compatibility filter
 *
 * @param {Object} urlQueryParams - Current URL query parameters
 * @param {Array} listingFieldsConfig - Listing fields configuration with enumOptions
 * @returns {string|null} - The compatibility name or null if no compatibility filter is active or multiple selected
 */
export const getActiveCompatibilityName = (urlQueryParams, listingFieldsConfig) => {
  if (!urlQueryParams || !listingFieldsConfig) {
    return null;
  }

  // Check for compatibility filter
  let compatibility = urlQueryParams.pub_compatibility || urlQueryParams.compatibility;

  if (compatibility) {
    // Remove search mode prefix (e.g., "has_any:" or "has_all:")
    if (compatibility.includes(':')) {
      compatibility = compatibility.split(':')[1];
    }

    // If multiple compatibilities (comma-separated), return null to use default title
    if (compatibility.includes(',')) {
      return null;
    }

    // Find the compatibility field config and get the label
    const compatibilityField = listingFieldsConfig.find(f => f.key === 'compatibility');
    if (compatibilityField && compatibilityField.enumOptions) {
      const option = compatibilityField.enumOptions.find(opt => opt.option === compatibility);
      if (option && option.label) {
        return option.label;
      }
    }

    // Fallback: Capitalize first letter and replace dashes with spaces
    return compatibility
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return null;
};

/**
 * Build a mapping of filter options to the amount of listings that match each option.
 *
 * @param {Array<Object>} listings - Listings returned by the current search query.
 * @param {Array<Object>} filterConfigs - Filter configurations rendered on the SearchPage.
 * @returns {Object} An object keyed by filter key, each containing a map of option value -> count.
 */
export const getFilterOptionCounts = (listings = [], filterConfigs = []) => {
  if (!Array.isArray(filterConfigs) || filterConfigs.length === 0) {
    return {};
  }

  const relevantConfigs = filterConfigs.filter(config => {
    const schemaType = config?.schemaType;
    return (
      schemaType === 'category' ||
      schemaType === 'listingType' ||
      schemaType === SCHEMA_TYPE_ENUM ||
      schemaType === SCHEMA_TYPE_MULTI_ENUM
    );
  });

  if (relevantConfigs.length === 0) {
    return {};
  }

  const counts = {};
  relevantConfigs.forEach(config => {
    counts[config.key] = {};
  });

  if (!Array.isArray(listings) || listings.length === 0) {
    return counts;
  }

  const getDataByScope = (listing, scope) => {
    const attributes = listing?.attributes || {};
    switch (scope) {
      case 'meta':
      case 'metadata':
        return attributes.metadata || {};
      case 'private':
        return attributes.privateData || {};
      case 'protected':
        return attributes.protectedData || {};
      default:
        return attributes.publicData || {};
    }
  };

  listings.forEach(listing => {
    relevantConfigs.forEach(config => {
      const { key, schemaType, scope } = config;
      const countsForFilter = counts[key] || (counts[key] = {});

      if (schemaType === 'category') {
        const publicData = listing?.attributes?.publicData || {};
        ['categoryLevel1', 'categoryLevel2', 'categoryLevel3'].forEach(levelKey => {
          const value = publicData[levelKey];
          if (value) {
            const normalized = `${value}`;
            countsForFilter[normalized] = (countsForFilter[normalized] || 0) + 1;
          }
        });
      } else if (schemaType === 'listingType') {
        const listingTypeValue = listing?.attributes?.publicData?.listingType;
        if (listingTypeValue) {
          const normalized = `${listingTypeValue}`;
          countsForFilter[normalized] = (countsForFilter[normalized] || 0) + 1;
        }
      } else {
        const scopedData = getDataByScope(listing, scope);
        const rawValue = scopedData?.[key];
        const values = Array.isArray(rawValue) ? rawValue : rawValue != null ? [rawValue] : [];

        values.forEach(val => {
          if (val != null) {
            const normalized = `${val}`;
            countsForFilter[normalized] = (countsForFilter[normalized] || 0) + 1;
          }
        });
      }
    });
  });

  return counts;
};

/**
 * SearchPage default-sort flags.
 * Order comes from the API (OpenSearch). Never shuffle on the client:
 * SSR vs hydrate would reshuffle the grid after first paint.
 */
export const resolveSearchFlags = ({
  normalizedRest = {},
  listingTypePathParam,
  isDealsPage = false,
  hasSlugPath = false,
} = {}) => {
  const hasKeywords = !!(normalizedRest.keywords || '').toString().trim();
  const hasExplicitSort = Object.prototype.hasOwnProperty.call(normalizedRest, 'sort');
  const hasActiveFilters = Object.keys(normalizedRest).some(
    key => !['sort', 'mapSearch', 'bounds'].includes(key)
  );
  const shouldRandomizeResults = false;
  const isDefaultSort = !hasExplicitSort && !hasKeywords;
  const shouldApplyPrioritySort =
    isDefaultSort && (hasActiveFilters || listingTypePathParam || isDealsPage || hasSlugPath);
  return {
    hasKeywords,
    hasExplicitSort,
    hasActiveFilters,
    shouldRandomizeResults,
    isDefaultSort,
    shouldApplyPrioritySort,
  };
};
